import React, { useEffect, useState } from 'react';
import {
  Card,
  Typography,
  Button,
  Space,
  message,
  Tabs,
  Spin,
  Progress,
} from 'antd';
import {
  ArrowLeftOutlined,
  DownloadOutlined,
  ReadOutlined,
  EyeOutlined,
} from '@ant-design/icons';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Letter, LetterSubTask, LetterItem } from '../services/letters';
import customerApi from '../services/customerApi';
import dayjs from 'dayjs';

const { Title, Text } = Typography;
const { TabPane } = Tabs;

export const CustomerLetterDetail: React.FC = () => {
  const { id } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [letter, setLetter] = useState<Letter | null>(null);
  const activeTab = searchParams.get('tab') || 'overview';

  useEffect(() => {
    if (id) {
      loadLetter(parseInt(id, 10));
    }
  }, [id]);

  const loadLetter = async (letterId: number) => {
    setLoading(true);
    try {
      const response = await customerApi.get(`/customer/letters/${letterId}`);
      setLetter(response.data.data || response.data);
    } catch (error: any) {
      console.error('Failed to load letter:', error);
      let errorMessage = 'Failed to load letter';
      
      if (error.response) {
        errorMessage = error.response.data?.message || error.response.data?.error || `Server error: ${error.response.status}`;
        if (error.response.status === 401) {
          errorMessage = 'Unauthorized. Please login again.';
        } else if (error.response.status === 403) {
          errorMessage = 'Access denied.';
        } else if (error.response.status === 404) {
          errorMessage = 'Letter not found.';
        }
      } else if (error.request) {
        errorMessage = 'Network error: Unable to connect to server';
      } else {
        errorMessage = error.message || 'Unknown error occurred';
      }
      
      message.error(errorMessage);
      navigate('/customer/personal');
    } finally {
      setLoading(false);
    }
  };

  const formatLetterNumber = (letterId: number, referenceNumber?: string) => {
    return referenceNumber || `#${letterId.toString().padStart(7, '0')}`;
  };

  const formatDateTime = (dateString?: string) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = date.getFullYear();
    const hours = date.getHours();
    const minutes = date.getMinutes().toString().padStart(2, '0');
    const ampm = hours >= 12 ? 'pm' : 'am';
    const displayHours = hours % 12 || 12;
    return `${day}/${month}/${year} ${displayHours}:${minutes}${ampm}`;
  };

  const handleTabChange = (key: string) => {
    setSearchParams({ tab: key });
  };

  const handleDownload = (attachment: string) => {
    const baseURL = customerApi.defaults.baseURL || '';
    const url = `${baseURL}/letter-items/${attachment}/download`;
    window.open(url, '_blank');
  };

  const handleRead = async () => {
    if (!id || !letter) return;
    try {
      await customerApi.patch(`/customer/letters/${id}/read`);
      message.success('Letter marked as read');
      // Reload letter to get updated status
      await loadLetter(parseInt(id, 10));
    } catch (error: any) {
      message.error('Failed to mark letter as read');
    }
  };

  const getLetterDisplayName = () => {
    return formatLetterNumber(letter?.id || 0, letter?.reference_number);
  };

  const getDisplayValue = (fieldName: string, type: 'select' | 'date' | 'number' | 'text' = 'text', options?: any[]) => {
    let value: any;
    if (fieldName === 'customer_id') {
      value = letter?.customer_id;
    } else if (fieldName === 'sender_id') {
      value = letter?.sender_id;
    } else if (fieldName === 'subject_id') {
      value = letter?.subject_id;
    } else if (fieldName === 'create_date') {
      value = letter?.create_date;
    } else if (fieldName === 'due_date') {
      value = letter?.due_date;
    } else if (fieldName === 'team_id') {
      value = letter?.team_id;
    }
    
    if (value === null || value === undefined || value === '') {
      return '-';
    }
    
    if (type === 'select' && options) {
      const option = options.find(opt => opt.id === value);
      return option?.name || '-';
    }
    
    if (type === 'date') {
      if (!value) return '-';
      return dayjs(value).format('DD/MM/YYYY');
    }
    
    if (type === 'number') {
      return value || '-';
    }
    
    return value || '-';
  };

  if (loading || !letter) {
    return (
      <div style={{ textAlign: 'center', padding: '50px' }}>
        <Spin size="large" />
        </div>
    );
  }

  const letterSubTasks = letter.subTasks || [];
  const letterItems = letter.items || [];
  const checklistPercent = (() => {
    const total = letterSubTasks.length;
    if (total === 0) return 0;
    const completed = letterSubTasks.filter((item: LetterSubTask) => item.completed).length;
    return Math.round((completed / total) * 100);
  })();

  // Map old status values to new ones
  const mapStatus = (status?: string): 'Plan' | 'Unread' | 'Read' => {
    if (!status) return 'Plan';
    if (status === 'draft') return 'Plan';
    if (status === 'sent') return 'Unread';
    if (['Plan', 'Unread', 'Read'].includes(status)) return status as 'Plan' | 'Unread' | 'Read';
    return 'Plan';
  };

  const statuses: Array<'Plan' | 'Unread' | 'Read'> = ['Plan', 'Unread', 'Read'];
  const statusConfig = {
    Plan: { bg: '#f5f5f5', border: '#d9d9d9', color: '#595959', dot: '#8c8c8c', label: 'Plan' },
    Unread: { bg: '#FFF7E6', border: '#FFA940', color: '#FF8C00', dot: '#FF8C00', label: 'Sent' },
    Read: { bg: '#F6FFED', border: '#73D13D', color: '#52C41A', dot: '#52C41A', label: 'Read' },
  };
  const currentStatus = mapStatus(letter.status);

  return (
    <div>
      {/* Header */}
      <div style={{ background: '#fff', padding: '16px 24px', borderRadius: 8 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <Title level={3} style={{ margin: 0 }}>
            {getLetterDisplayName()}
          </Title>
          <Space>
            {currentStatus === 'Unread' && (
              <Button
                type="primary"
                icon={<ReadOutlined />}
                onClick={handleRead}
              >
                Read
              </Button>
            )}
            <Button
              icon={<ArrowLeftOutlined />}
              onClick={() => navigate('/customer/personal')}
            >
              Back
            </Button>
          </Space>
        </div>
      </div>

      {/* Tabs */}
      <Card style={{ background: '#fff' }}>
        <Tabs 
          activeKey={activeTab}
          onChange={handleTabChange}
          tabBarExtraContent={
            <Space size={8}>
              {statuses.map((status) => {
                const isActive = currentStatus === status;
                const config = statusConfig[status];
                return (
                  <div
                    key={status}
                    style={{
                      padding: '4px 12px',
                      borderRadius: '12px',
                      backgroundColor: isActive ? config.bg : '#f5f5f5',
                      border: isActive ? `1px solid ${config.border}` : '1px solid #d9d9d9',
                      color: isActive ? config.color : '#595959',
                      fontWeight: isActive ? 500 : 400,
                      fontSize: '12px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 4,
                      transition: 'all 0.2s',
                    }}
                  >
                    <span style={{
                      width: 4,
                      height: 4,
                      borderRadius: '50%',
                      backgroundColor: isActive ? config.dot : '#8c8c8c',
                    }}></span>
                    {config.label}
                  </div>
                );
              })}
            </Space>
          }
        >
          {/* Overview Tab */}
          <TabPane tab="Overview" key="overview">
            <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
              <div style={{ flex: 1, minWidth: 320 }}>
                <Card style={{ marginBottom: 8 }} bodyStyle={{ padding: 0 }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <tbody>
                      {/* Customer Type - 单独一行，合并后三列 */}
                      <tr style={{ borderBottom: '1px solid #f0f0f0' }}>
                        <td style={{ padding: '12px', width: '120px', verticalAlign: 'top', backgroundColor: '#fafafa' }}>
                          <strong>Customer</strong>
                        </td>
                        <td colSpan={3} style={{ padding: '12px', verticalAlign: 'top' }}>
                          <div>
                            {(letter as any).business ? 'Business' : 'Personal'}
                          </div>
                        </td>
                      </tr>
                      {/* Customer/Business - 单独一行，合并后三列 */}
                      <tr style={{ borderBottom: '1px solid #f0f0f0' }}>
                        <td style={{ padding: '12px', width: '120px', verticalAlign: 'top', backgroundColor: '#fafafa' }}>
                          <strong>{(letter as any).business ? 'Business' : 'Customer'}</strong>
                        </td>
                        <td colSpan={3} style={{ padding: '12px', verticalAlign: 'top' }}>
                          <div>
                            {(letter as any).business ? (letter as any).business?.name : letter.customer?.name || '-'}
                          </div>
                        </td>
                      </tr>
                      {/* Sender - 单独一行，合并后三列 */}
                      <tr style={{ borderBottom: '1px solid #f0f0f0' }}>
                        <td style={{ padding: '12px', width: '120px', verticalAlign: 'top', backgroundColor: '#fafafa' }}>
                          <strong>Sender</strong>
                        </td>
                        <td colSpan={3} style={{ padding: '12px', verticalAlign: 'top' }}>
                          <div>
                            {letter.taskType?.name || letter.sender?.name || '-'}
                          </div>
                        </td>
                      </tr>
                      {/* Subject - 单独一行，合并后三列 */}
                      <tr style={{ borderBottom: '1px solid #f0f0f0' }}>
                        <td style={{ padding: '12px', width: '120px', verticalAlign: 'top', backgroundColor: '#fafafa' }}>
                          <strong>Subject</strong>
                        </td>
                        <td colSpan={3} style={{ padding: '12px', verticalAlign: 'top' }}>
                          <div>
                            {letter.taskTemplate?.name || letter.subject?.name || '-'}
                          </div>
                        </td>
                      </tr>
                      {/* Create 和 Due - 同一行，各占两列 */}
                      <tr style={{ borderBottom: '1px solid #f0f0f0' }}>
                        <td style={{ padding: '12px', width: '120px', verticalAlign: 'top', backgroundColor: '#fafafa' }}>
                          <strong>Create</strong>
                        </td>
                        <td style={{ padding: '12px', width: 'calc((100% - 240px) / 2)', verticalAlign: 'top' }}>
                          <div>
                            {getDisplayValue('create_date', 'date')}
                          </div>
                        </td>
                        <td style={{ padding: '12px', width: '120px', verticalAlign: 'top', backgroundColor: '#fafafa' }}>
                          <strong>Due</strong>
                        </td>
                        <td style={{ padding: '12px', width: 'calc((100% - 240px) / 2)', verticalAlign: 'top' }}>
                          <div>
                            {getDisplayValue('due_date', 'date')}
                          </div>
                        </td>
                      </tr>
                      {/* Team - 单独一行，合并后三列 */}
                      <tr style={{ borderBottom: '1px solid #f0f0f0' }}>
                        <td style={{ padding: '12px', width: '120px', verticalAlign: 'top', backgroundColor: '#fafafa' }}>
                          <strong>Team</strong>
                        </td>
                        <td colSpan={3} style={{ padding: '12px', verticalAlign: 'top' }}>
                          <div>
                            {letter.team?.name || '-'}
                          </div>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </Card>
              </div>
              <Card style={{ width: 220, textAlign: 'center' }}>
                <Text type="secondary">Progress</Text>
                <div style={{ marginTop: 12 }}>
                  <Progress type="circle" percent={checklistPercent} width={96} />
                </div>
                <div style={{ marginTop: 8 }}>
                  <Text type="secondary">{checklistPercent}%</Text>
                </div>
              </Card>
            </div>
          </TabPane>

          {/* Letter Tab */}
          <TabPane tab="Letter" key="letter">
            <Card
              title="Letters"
              bodyStyle={{ padding: '16px' }}
            >
              {letterItems.map((item: LetterItem) => {
                const fileName = item.attachment ? item.attachment.split('/').pop() : 'No attachment';
                return (
                  <div key={item.id} style={{ padding: '12px 0', borderBottom: '1px solid #f0f0f0' }}>
                    <div style={{ marginBottom: 4 }}>
                      {item.attachment ? (
                        <Space>
                          <Button
                            type="link"
                            icon={<EyeOutlined />}
                            onClick={() => {
                              const baseURL = customerApi.defaults.baseURL || '';
                              const url = `${baseURL}/letter-items/${item.attachment}/view`;
                              window.open(url, '_blank');
                            }}
                            size="small"
                          >
                            {fileName}
                          </Button>
                          <Button
                            type="link"
                            icon={<DownloadOutlined />}
                            onClick={() => handleDownload(item.attachment!)}
                            size="small"
                          />
                        </Space>
                      ) : (
                        <Text strong>No attachment</Text>
                      )}
                    </div>
                    <div style={{ fontSize: '12px', color: '#999' }}>
                      By {item.creator?.name || 'Unknown'} {formatDateTime(item.created_at)}
                    </div>
                  </div>
                );
              })}
              {letterItems.length === 0 && (
                <div style={{ color: '#999', textAlign: 'center', padding: '20px 0' }}>
                  No letter items
                </div>
              )}
            </Card>
          </TabPane>

          </Tabs>
    </Card>
    </div>
  );
};
